"use client"

import { Users } from "lucide-react"
import { ROLE_BG, DEFAULT_ROLE_BG } from "@/lib/community/showcase-constants"
import type { CommunityPost } from "@/lib/community/types"

type TeamAgent = NonNullable<CommunityPost["teamDetail"]>["agents"][number]

interface TeamShowcaseProps {
  agents: TeamAgent[]
  agentNames?: Map<string, string>
}

export function TeamShowcase({ agents }: TeamShowcaseProps) {
  const roles = agents.map((a) => a.role)
  const uniqueRoles = [...new Set(roles)]
  const totalTools = agents.reduce((sum, a) => sum + a.toolCount, 0)
  const totalSkills = agents.reduce((sum, a) => sum + a.skillNames.length, 0)

  if (agents.length === 0) return null

  const nameMap = new Map(agents.map((a) => [a.id, a.name]))

  return (
    <div className="mb-5 space-y-2 w-fit">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
        <Users className="h-3.5 w-3.5 text-red-500" />
        团队构成
      </h4>

      <div className="rounded-xl overflow-hidden border border-red-500/30">
        {/* Header */}
        <div
          className="px-3 py-2 flex items-center gap-2 bg-red-500/10"
        >
          <div className="flex -space-x-1.5">
            {agents.slice(0, 6).map((agent, i) => (
              <div
                key={agent.id}
                className="h-5 w-5 rounded-full border-[1.5px] border-background flex items-center justify-center text-[8px] font-bold"
                style={{
                  background: `hsl(${(i * 360) / agents.length}, 40%, 70%)`,
                  color: "white",
                  zIndex: agents.length - i,
                }}
              >
                {agent.name.charAt(0)}
              </div>
            ))}
            {agents.length > 6 && (
              <div className="h-5 w-5 rounded-full border-[1.5px] border-background bg-secondary/70 flex items-center justify-center text-[8px] text-muted-foreground font-medium">
                +{agents.length - 6}
              </div>
            )}
          </div>
          <span className="text-caption font-semibold text-foreground/80">
            智能体团队构成 · {agents.length} 位成员
          </span>
        </div>

        {/* Members */}
        <div className="px-2 py-2 space-y-1">
          {agents.map((agent) => {
            const depNames = agent.dependencies
              .map((depId) => nameMap.get(depId))
              .filter(Boolean)

            return (
              <div
                key={agent.id}
                className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 bg-secondary/15"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-caption font-semibold text-foreground truncate">
                      {agent.name}
                    </span>
                    <span
                      className={`text-nano px-1.5 py-0.5 rounded-full ${ROLE_BG[agent.role] ?? DEFAULT_ROLE_BG}`}
                    >
                      {agent.role}
                    </span>
                  </div>
                  {depNames.length > 0 && (
                    <p className="text-nano text-muted-foreground/60 mt-0.5">
                      依赖：{depNames.join("、")}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-1.5 text-nano text-muted-foreground shrink-0">
                  <span>{agent.skillNames.length} 技能</span>
                  <span className="text-border">·</span>
                  <span>{agent.toolCount} 工具</span>
                </div>
              </div>
            )
          })}
        </div>

        {/* Footer stats */}
        <div className="px-3 py-1.5 border-t border-red-500/20 flex items-center gap-3 text-micro text-muted-foreground">
          <span>共 {totalSkills} 个技能</span>
          <span>·</span>
          <span>{totalTools} 次工具调用</span>
          <span>·</span>
          <span>{uniqueRoles.length} 个角色</span>
        </div>
      </div>
    </div>
  )
}
